/**
 * トップページ。
 * 会員登録なしで、その場でグループを作って使い始められることを最優先にしている。
 */
import { createRecentGroupStore, useCreateGroup, useSpli } from "@spli/app-logic";
import { CURRENCY_CODES, getCurrency, type CurrencyCode } from "@spli/core";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorView } from "../components/StatusViews.js";

/**
 * 「スプリでできること」の 4 枚。
 *
 * 他の割り勘アプリでは諦めることになる場面だけを選んでいる。
 * 言葉で説明すると長くなるので、まずイラスト (public/tips*.png) で状況を見せ、
 * 一文だけ添える。
 *
 * 本文は表示する 1 行ずつに分けて持つ。幅に任せると「金額を / 変更」のように
 * 語の途中で割れたり、カードごとに行数が変わって落ち着かないので、
 * 意味の切れ目で必ず改行する (1 行が入り切らないときだけ、さらに折り返す)。
 */
const FEATURES: Array<{ image: string; lines: ReactNode[] }> = [
  {
    image: "/tips1.png",
    lines: [
      <>
        お酒を飲まなかった人だけ<strong>金額を変更</strong>。
      </>,
      <>残りは自動で割り直します。</>,
    ],
  },
  {
    image: "/tips2.png",
    lines: [
      <>
        <strong>誰かの分を別の人が負担</strong>。
      </>,
      <>子どもや後輩の分をまとめて出せます。</>,
    ],
  },
  {
    image: "/tips3.png",
    lines: [
      <>「この人には直接送りたくない」</>,
      <>
        を指定すると<strong>別の人を経由</strong>します。
      </>,
    ],
  },
  {
    image: "/tips4.png",
    lines: [
      <>
        受け取った本人が<strong>受取完了を記録</strong>。
      </>,
      <>「払ったつもり」を防ぎます。</>,
    ],
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useSpli();
  const createGroup = useCreateGroup();
  const recentStore = useMemo(() => createRecentGroupStore(), []);
  const recent = useMemo(() => recentStore.list(), [recentStore]);

  const [name, setName] = useState("");
  const [currency, setCurrency] = useState<CurrencyCode>("JPY");
  const [memberText, setMemberText] = useState("");

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const memberNames = memberText
      .split(/[,、\n]/)
      .map((s) => s.trim())
      .filter((s) => s !== "");

    createGroup.mutate(
      { name: name.trim(), currency, memberNames },
      {
        onSuccess: (group) => {
          recentStore.remember({ shareToken: group.shareToken, name: group.name });
          navigate(`/g/${group.shareToken}`);
        },
      },
    );
  };

  return (
    <>
      {/*
        ヒーロー: PC では「左に説明・右に作成フォーム」の 2 カラム。
        スマホでは 1 カラムに落ち、説明を短く保つことで
        スクロールせずに作成フォームが見えるようにしている
        (詳しい説明はフォームより下に置く)。
      */}
      <section className="home-hero">
        <div className="home-hero__intro">
          {/*
            見出しは画像 1 枚で出す (アイコン・ロゴ・キャッチコピーが入っている)。
            読み上げと検索には alt が使われるので、h1 の中身として置く。
            画像は原本から透過の余白を切り落としてあり、周囲の間隔は CSS で決める。
          */}
          <h1 className="home-hero__brand">
            <img
              src="/hero.png"
              alt="スプリ — かゆいところに手が届く割り勘計算アプリ"
              width={941}
              height={402}
            />
          </h1>

          {/*
            会員登録が要らないことは、この画面で唯一ここだけが伝えている。
            見出しの主役は画像なので、添え書きとして小さく置く。
          */}
          <p className="home-hero__note">
            会員登録は不要。URL を共有するだけで、みんなで支出を記録して精算できます。
          </p>

        </div>

        <section className="card home-hero__form">
          <h2 className="card__title">新しいグループをつくる</h2>
          <form onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="group-name">
                グループ名
              </label>
              <input
                id="group-name"
                className="input"
                value={name}
                placeholder="例: 沖縄旅行 2026"
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="group-members">
                メンバー (カンマまたは改行区切り・あとから追加できます)
              </label>
              <textarea
                id="group-members"
                className="textarea"
                value={memberText}
                placeholder={"けん\nまい\nしょう"}
                onChange={(event) => setMemberText(event.target.value)}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="group-currency">
                通貨
              </label>
              <select
                id="group-currency"
                className="select"
                value={currency}
                onChange={(event) => setCurrency(event.target.value as CurrencyCode)}
              >
                {CURRENCY_CODES.map((code) => (
                  <option key={code} value={code}>
                    {getCurrency(code).label} ({getCurrency(code).symbol})
                  </option>
                ))}
              </select>
            </div>

            {createGroup.isError && <ErrorView error={createGroup.error} />}

            <button
              type="submit"
              className="button button--block"
              disabled={createGroup.isPending || name.trim() === ""}
            >
              {createGroup.isPending ? "作成中…" : "グループをつくる"}
            </button>
          </form>
        </section>
      </section>

      {/* --- スプリの説明 (フォームの下) --- */}
      <section className="home-about">
        <h2 className="home-about__title">スプリでできること</h2>
        <div className="feature-grid">
          {FEATURES.map((feature) => (
            <div className="feature" key={feature.image}>
              {/*
                イラストは本文と同じことを言っているので、読み上げでは重複になる。
                装飾として扱い、意味は本文に任せる (alt="")。
              */}
              <img
                className="feature__image"
                src={feature.image}
                alt=""
                width={440}
                height={484}
                loading="lazy"
                decoding="async"
              />
              <p className="feature__text">
                {feature.lines.map((line, index) => (
                  <span className="feature__line" key={index}>
                    {line}
                  </span>
                ))}
              </p>
            </div>
          ))}
        </div>
      </section>

      {recent.length > 0 && (
        <section className="card" style={{ marginTop: 24 }}>
          <h2 className="card__title">最近開いたグループ</h2>
          <div className="list">
            {recent.slice(0, 5).map((group) => (
              <Link key={group.shareToken} className="list-item" to={`/g/${group.shareToken}`}>
                <span className="list-item__body">
                  <span className="list-item__title">{group.name}</span>
                  <span className="list-item__meta">
                    最終アクセス {group.openedAt.slice(0, 10)}
                  </span>
                </span>
              </Link>
            ))}
          </div>
          <p className="muted" style={{ marginTop: 12 }}>
            この一覧はこの端末にのみ保存されます。
            {!user && (
              <>
                {" "}
                <Link to="/login">ログイン</Link>すると、どの端末からでも履歴を見られます。
              </>
            )}
          </p>
        </section>
      )}
    </>
  );
}
